import axios from 'axios';
import requestManager, { withRequestManagement } from '../utils/requestManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Global cache to prevent repeated location requests
let locationRequestCache = {
  inProgress: false,
  lastAttempt: 0,
  cooldownPeriod: 30000 // 30 seconds
};

// Use the same global throttling as riderService
let apiRequestThrottle = {
  lastUpdate: 0,
  cooldownPeriod: 15000, // 15 seconds minimum between updates
  pendingRequests: new Set(),
  maxConcurrentRequests: 1, // Only 1 concurrent location update
  failureCount: 0,
  lastFailure: 0,
  backoffMultiplier: 1,
  circuitBreakerOpen: false,
  circuitBreakerTimeout: 0
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

export const locationService = {
  // Update user location with global request management
  updateLocation: withRequestManagement(async (latitude, longitude, address = null) => {
    const response = await axios.put(
      `${API_URL}/location/update`,
      { latitude, longitude, address },
      {
        ...getAuthHeaders(),
        timeout: 20000 // 20 second timeout
      }
    );
    return response.data;
  }, 'location-update'),

  // Get user's current location
  getCurrentLocation: async () => {
    const response = await axios.get(
      `${API_URL}/location/current`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get nearby pharmacies
  getNearbyPharmacies: async (latitude, longitude, maxDistance = 10) => {
    const response = await axios.get(
      `${API_URL}/location/nearby-pharmacies?latitude=${latitude}&longitude=${longitude}&maxDistance=${maxDistance}`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get nearby riders
  getNearbyRiders: async (latitude, longitude, maxDistance = 5) => {
    const response = await axios.get(
      `${API_URL}/location/nearby-riders?latitude=${latitude}&longitude=${longitude}&maxDistance=${maxDistance}`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get all pharmacies with location
  getAllPharmaciesWithLocation: async () => {
    const response = await axios.get(
      `${API_URL}/location/pharmacies`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get all riders with location
  getAllRidersWithLocation: async () => {
    const response = await axios.get(
      `${API_URL}/location/riders`,
      getAuthHeaders()
    );
    return response.data;
  },

  // Get user's current position using browser geolocation
  getCurrentPosition: () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      // Check if we're in cooldown period
      const now = Date.now();
      if (locationRequestCache.inProgress) {
        reject(new Error('Location request already in progress'));
        return;
      }

      if (now - locationRequestCache.lastAttempt < locationRequestCache.cooldownPeriod) {
        reject(new Error('Location request in cooldown period'));
        return;
      }

      // Set cache state
      locationRequestCache.inProgress = true;
      locationRequestCache.lastAttempt = now;

      // Try with less strict options first to avoid timeouts
      navigator.geolocation.getCurrentPosition(
        (position) => {
          locationRequestCache.inProgress = false;
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.warn('Geolocation error:', error.message);
          locationRequestCache.inProgress = false;
          
          // Don't try fallback for permission or timeout errors
          if (error.code === error.PERMISSION_DENIED || error.code === error.TIMEOUT) {
            reject(error);
            return;
          }
          
          // Try with even less strict options
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy
              });
            },
            (fallbackError) => {
              console.warn('Fallback geolocation also failed:', fallbackError.message);
              reject(fallbackError);
            },
            {
              enableHighAccuracy: false,
              timeout: 5000,
              maximumAge: 300000 // 5 minutes
            }
          );
        },
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  },

  // Request location permission
  requestLocationPermission: async () => {
    try {
      const position = await locationService.getCurrentPosition();
      return {
        success: true,
        position,
        message: 'Location permission granted'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Location permission denied or unavailable'
      };
    }
  }
};
